import React, { useCallback, useEffect, useMemo } from "react";

import moment from "moment";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { NumberParam, StringParam, useQueryParam, useQueryParams, withDefault } from "use-query-params";

import { Table } from "antd";
import { SorterResult } from "antd/lib/table/interface";

import { Page } from "../../components/page";
import { PageHeader } from "../../components/page_header";
import { Searcher } from "../../components/searcher";
import { DateRange, MarketSelector } from "../../components/selectors";
import { ColumnTitle, TableNotification } from "../../components/table";
import { iataFetchRequest, reportGeneratorTaskCreateRequest, tableFetchRequest } from "../../store/airlines/actions";
import { AirlinesItem } from "../../store/airlines/types";
import { ReportFormat } from "../../store/common_types";
import { RootState } from "../../store/root_reducer";
import { MomentParam } from "../../utils/moment_param";
import { formatNumber, getSortOrder } from "../../utils/table";

export const AirlinesPage: React.FC = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation(["airlines_page", "common"]);

  // -- Hooks Start --
  const [dateRange, setDateRange] = useQueryParams({
    from: withDefault(MomentParam, moment.utc().startOf("day").subtract(1, "week").add(1, "day")),
    to: withDefault(MomentParam, moment.utc().startOf("day")),
  });
  const [pagination, setPagination] = useQueryParams({
    page: withDefault(NumberParam, 1),
    pageSize: withDefault(NumberParam, 10),
  });
  const [market, setMarket] = useQueryParam("market", withDefault(StringParam, undefined));
  const [search, setSearch] = useQueryParam("search", withDefault(StringParam, undefined));
  const [ordering, setOrdering] = useQueryParam("ordering", withDefault(StringParam, "-sumClicksCount"));
  // -- Hooks End --

  // -- Stores Start --
  const reportState = useSelector((state: RootState) => state.Airlines);
  const markets = useSelector((state: RootState) => state.Markets.markets);
  const selectedGate = useSelector((state: RootState) => state.User.selectedGate);
  // -- Stores End --

  // -- Custom Handlers Start --
  const onExpand = (iata: string) => {
    if (!(iata in reportState.itemsByIata)) {
      dispatch(
        iataFetchRequest({
          iata,
          market,
          to: dateRange.to,
          gateId: selectedGate.id,
          from: dateRange.from,
          page: pagination.page,
          pageSize: pagination.pageSize,
        })
      );
    }
  };
  const chooseDateRange = (value: DateRange) => {
    setDateRange(value);
    setPagination({ ...pagination, page: 1 }, "replaceIn");
  };
  const chooseMarket = (value?: string) => {
    setMarket(value);
    setPagination({ ...pagination, page: 1 }, "replaceIn");
  };
  // -- Custom Handlers End --

  // -- Hook Effects Start --
  useEffect(() => {
    setDateRange((state) => state, "replaceIn");
    setPagination((state) => state, "replaceIn");
    setMarket((state) => state, "replaceIn");
    setSearch((state) => state, "replaceIn");
    setOrdering((state) => state, "replaceIn");
  }, [setDateRange, setMarket, setOrdering, setPagination, setSearch]);

  const fetch = useCallback(() => {
    dispatch(
      tableFetchRequest({
        market,
        ordering,
        search,
        to: dateRange.to,
        gateId: selectedGate.id,
        from: dateRange.from,
        page: pagination.page,
        pageSize: pagination.pageSize,
      })
    );
  }, [dispatch, dateRange, pagination, market, ordering, selectedGate, search]);

  const downloadReport = useCallback(
    (reportFormat: ReportFormat) => {
      dispatch(
        reportGeneratorTaskCreateRequest({
          ...dateRange,
          market,
          search,
          ordering,
          reportFormat,
          gateId: selectedGate.id,
        })
      );
    },
    [dispatch, market, ordering, dateRange, selectedGate, search]
  );

  useEffect(() => {
    fetch();
  }, [fetch]);
  // -- Hook Effects End --

  const fields = [
    "airlineIata",
    "sumClicksCount",
    "sumUniqueClicksCount",
    "sumSales",
    "avgStr",
    "avgClicksShare",
    "avgProductivity",
  ] as Array<keyof AirlinesItem>;
  const columns = fields.map((field, i) => ({
    title: <ColumnTitle title={t(`table.columns.${field}.title`)} helpText={t(`table.columns.${field}.help`)} />,
    key: field,
    dataIndex: field,
    sorter: true,
    sortOrder: getSortOrder(field, ordering),
    render: (text: string, record: AirlinesItem) => {
      if (field !== "airlineIata" && field !== "items") {
        return formatNumber(record[field]);
      }
      return text;
    },
  }));

  const dataSource = useMemo(() => {
    const source = reportState.items;
    if (!reportState.isLoading && reportState.items.length > 0) {
      for (const item of source) {
        item.items = reportState.itemsByIata[item.airlineIata] || [];

        for (const subItem of item.items) {
          subItem.airlineIata = subItem.pdate;
        }
      }
    }
    return source;
  }, [reportState.items, reportState.itemsByIata, reportState.isLoading]);

  const handleSearch = (value: string) => {
    setSearch(value || undefined);
    setPagination((state) => ({ ...state, page: 1 }));
  };

  return (
    <Page title={t("page.title")} description={t("page.description")} reportTTL={365} report="airlines">
      <PageHeader
        onReload={fetch}
        dateRange={dateRange}
        isLoading={reportState.isLoading}
        onDateRangeSelect={chooseDateRange}
        onDownloadReport={downloadReport}
        reportDownloadDisabled={reportState.count === 0}
      >
        <MarketSelector markets={markets} value={market} onSelect={chooseMarket} />
        <Searcher onSearch={handleSearch} loading={reportState.isLoading} defaultValue={search} />
      </PageHeader>
      <TableNotification text={t("tableNotification")} />
      <Table
        bordered
        size="small"
        columns={columns}
        scroll={{ x: "auto" }}
        dataSource={dataSource}
        childrenColumnName="items"
        loading={reportState.isLoading}
        footer={() => `${t("common:table.total")}: ${reportState.count}`}
        pagination={{
          total: reportState.count,
          current: pagination.page,
          pageSize: pagination.pageSize,
          showSizeChanger: true,
          pageSizeOptions: ["10", "25", "100"],
          onChange: (page, pageSize) => {
            if (page && pageSize) {
              setPagination((currentPagination) => ({
                ...currentPagination,
                pageSize,
                page: pageSize !== currentPagination.pageSize ? 1 : page,
              }));
            }
          },
        }}
        showSorterTooltip={false}
        sortDirections={["ascend", "descend", "ascend"]}
        onChange={(_, filters, sorter: SorterResult<AirlinesItem> | SorterResult<AirlinesItem>[], extra) => {
          if (!Array.isArray(sorter)) {
            setOrdering(`${sorter.order === "descend" ? "-" : ""}${sorter.column?.dataIndex}`);
          }
        }}
        rowKey={(record: AirlinesItem) => `airlines-row-${record.airlineIata}`}
        onExpand={(expanded, record) => expanded && onExpand(record.airlineIata)}
        expandedRowKeys={Object.keys(reportState.itemsByIata).length === 0 ? [] : undefined}
      />
    </Page>
  );
};
